import { Star, Quote, MessageCircle, MapPin } from "lucide-react";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schemas";
import { REVIEWS, TRUST, WHATSAPP_URL, SOCIAL } from "../data/site";

// Extended testimonials list (curated, anonymised first-name-only)
const EXTRA_REVIEWS = [
  { name: "Vikas R.", location: "Sigra, Varanasi", text: "Father's variceal bleeding was managed in time. Dr. Dixit explained every step, kept us calm, and the cirrhosis is now stable.", rating: 5 },
  { name: "Pooja A.", location: "Sarnath", text: "Came with chronic bloating that two clinics dismissed as 'gas'. SIBO breath test confirmed it, two weeks of rifaximin and the bloating is finally gone.", rating: 5 },
  { name: "Mohit K.", location: "Lanka", text: "Hepatitis C SVR12 achieved after 12-week DAA course. Cured at 34. Cannot thank the team enough.", rating: 5 },
  { name: "Asha P.", location: "Varanasi Cantt", text: "Endoscopy at age 52 — caught a pre-cancerous polyp early. Removed in same sitting. Surveillance every 3 years now. Genuine medicine.", rating: 5 },
  { name: "Imran S.", location: "Chetganj", text: "Ulcerative colitis on biologic for 18 months — first time in years I haven't had a flare. The follow-ups are detailed and unhurried.", rating: 5 },
  { name: "Geeta V.", location: "BHU campus", text: "FibroScan twice a year, lost 9 kg, my fatty liver is now normal. Methodical, kind care.", rating: 5 },
  { name: "Suresh L.", location: "Lahurabir", text: "Difficult ERCP for bile duct stones — done in one sitting at the partner hospital. Recovered well, no surgery needed.", rating: 5 },
  { name: "Naina B.", location: "Varanasi", text: "Chronic constipation for 15 years. Two months of structured plan and I am off all laxatives. Wish I'd come earlier.", rating: 5 },
];

const ALL_REVIEWS = [...REVIEWS, ...EXTRA_REVIEWS];

export default function Testimonials() {
  return (
    <main data-testid="testimonials-page">
      <SEO
        title="Patient Testimonials — Dr. Indresh Dixit, Gastroenterologist, Varanasi"
        description="Real Google Reviews and patient stories for Dr. Indresh Dixit, DM Gastroenterology (BHU), Varanasi. 900+ reviews, 4.8/5 average rating."
        canonical="/testimonials"
        schemas={[
          physicianSchema(),
          ...medicalClinicSchemas(),
          webPageSchema({
            name: "Patient Testimonials — Dr. Indresh Dixit",
            description: "Real patient testimonials and 4.8/5 Google rating.",
            url: "/testimonials",
          }),
          breadcrumbSchema([
            { label: "Home", url: "/" },
            { label: "Testimonials" },
          ]),
        ]}
      />

      <section className="border-b border-black/10">
        <div className="container-x py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="overline">Patient Testimonials</div>
            <h1
              data-testid="testimonials-h1"
              className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight"
            >
              {TRUST.reviews} reviews. {TRUST.rating}/5.{" "}
              <span className="text-[#0370AD]">One conviction.</span>
            </h1>
            <p className="mt-7 text-black/70 max-w-2xl">
              Real patient stories — anonymised to first name and locality.
              Verified ratings come from Google. Anyone can leave a review on
              our clinic profiles, linked below.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://maps.app.goo.gl/bSxnikJgGsFyg3Gg6"
                target="_blank"
                rel="noreferrer"
                data-testid="testimonials-google-lanka"
                className="btn-ghost"
              >
                <MapPin className="h-4 w-4" /> Review on Google — Lanka
              </a>
              <a
                href="https://maps.app.goo.gl/MA34vXRyD8tUHGUn7"
                target="_blank"
                rel="noreferrer"
                data-testid="testimonials-google-chetganj"
                className="btn-ghost"
              >
                <MapPin className="h-4 w-4" /> Review on Google — Chetganj
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 flex lg:items-end lg:justify-end">
            <div className="flex flex-col items-start lg:items-end">
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-6 w-6 fill-[#F5B400] text-[#F5B400]" />
                ))}
              </div>
              <div className="mt-3 font-serif-display text-7xl leading-none">
                {TRUST.rating}
                <span className="text-2xl align-top text-black/55">/5</span>
              </div>
              <div className="mt-2 text-[12px] tracking-[0.18em] uppercase text-black/55 font-semibold">
                Google · Verified
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews grid */}
      <section className="section-pad bg-[#fbfaf7]">
        <div className="container-x">
          <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight mb-10">
            What patients say in Varanasi.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALL_REVIEWS.map((r, i) => (
              <div
                key={i}
                data-testid={`testimonial-card-${i}`}
                className="bg-white border border-black/10 rounded-[6px] p-7 flex flex-col"
              >
                <Quote className="h-6 w-6 text-[#0370AD]" />
                <p className="mt-4 text-black/80 leading-relaxed flex-1">
                  {r.text}
                </p>
                <div className="mt-6 pt-5 border-t border-black/10 flex items-center justify-between">
                  <div>
                    <h3 className="font-display-semibold text-[16px]">{r.name}</h3>
                    <div className="text-[12px] text-black/55">{r.location}</div>
                  </div>
                  <div className="flex">
                    {Array.from({ length: r.rating }).map((_, j) => (
                      <Star key={j} className="h-3.5 w-3.5 fill-[#F5B400] text-[#F5B400]" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-[#0a0a0a] text-white">
        <div className="container-x py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-8">
            <div className="overline !text-[#7ec3e6]">Your story next?</div>
            <p className="font-serif-display mt-3 text-3xl md:text-5xl leading-tight">
              Book a consult — and tell us how it went.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3 md:justify-end">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="testimonials-book-btn"
              className="btn-primary"
            >
              <MessageCircle className="h-4 w-4" /> Book on WhatsApp
            </a>
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/30 text-white font-semibold text-[14px] hover:bg-white hover:text-black transition-colors"
            >
              Follow on Facebook
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
